import { useProfileContext } from '@components/Contexts/ProfileContext';
import { useProfilePageBi } from '@components/Hooks/useProfilePageBi';
import ArrowSvg from '@components/Images/ArrowSvg';
import {
  CLIENT_PROJECTS_PORTFOLIO_INCREMENT,
  CLIENT_PROJECTS_PORTFOLIO_INITIAL_DISPLAY,
  CLIENT_PROJECTS_PORTFOLIO_MOBILE_INCREMENT,
} from '@utils/constants';
import { Box, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import clsx from 'clsx';
import _ from 'lodash';
import { FC, useCallback, useEffect, useState } from 'react';
import { animateScroll } from 'react-scroll';
import s from './ClientProjects.module.scss';
import ProjectGrid from './ProjectGrid/ProjectGrid';

const scrollToClientProjects = () => {
  const element = document.getElementById('client-projects');
  if (element) {
    const offset = element.getBoundingClientRect().top - 180;
    animateScroll.scrollMore(offset, { duration: 0 });
  }
};

type Props = {
  isMobileView?: boolean;
};

const ClientProjects: FC<Props> = ({ isMobileView }) => {
  const { t } = useTranslation();
  const { partnerProfile } = useProfileContext();
  const { logProfilePageClick } = useProfilePageBi();

  useEffect(() => {
    setImageSlideWidth(
      isMobileView ? (window.screen.width * 93.6) / 100 + 'px' : 'auto',
    );
  }, [isMobileView]);

  const [quantityToDisplay, setQuantityToDisplay] = useState<number>(
    CLIENT_PROJECTS_PORTFOLIO_INITIAL_DISPLAY,
  );
  const [imageSlideWidth, setImageSlideWidth] = useState('auto');

  const maxLength = _.size(partnerProfile.portfolio?.items);
  const shouldDisplayShowMore =
    maxLength > CLIENT_PROJECTS_PORTFOLIO_INITIAL_DISPLAY;
  const remainingLength = maxLength - quantityToDisplay;
  const haveMoreToShow = remainingLength > 0;

  const logShowMoreClick = () => {
    logProfilePageClick('show more results');
  };

  const onClickShowMoreOrLess = useCallback(() => {
    if (haveMoreToShow) {
      // Show More
      const increment = Math.min(
        remainingLength,
        isMobileView
          ? CLIENT_PROJECTS_PORTFOLIO_MOBILE_INCREMENT
          : CLIENT_PROJECTS_PORTFOLIO_INCREMENT,
      );
      setQuantityToDisplay((prev) => prev + increment);
      logShowMoreClick();
    } else {
      // Show Less
      setQuantityToDisplay(CLIENT_PROJECTS_PORTFOLIO_INITIAL_DISPLAY);
      scrollToClientProjects();
    }
  }, [haveMoreToShow, remainingLength, logShowMoreClick]);

  return (
    <Box
      direction="vertical"
      dataHook="client-projects"
      width={imageSlideWidth}
      className={clsx(s.projectsContainer, isMobileView && s.mobile)}
    >
      <ProjectGrid
        projects={_.get(partnerProfile, 'portfolio.items', [])}
        quantityToDisplay={quantityToDisplay}
        isMobileView={isMobileView}
      />

      {shouldDisplayShowMore && (
        <Box align="right" marginTop="18px">
          <TextButton
            dataHook="show-more-less-button"
            skin="dark"
            underline="always"
            size="medium"
            weight="normal"
            onClick={onClickShowMoreOrLess}
            className={s.showMoreLessButton}
            suffixIcon={
              <Box
                className={`${s.showMoreLessButtonIcon} ${
                  haveMoreToShow ? s.isMore : s.isLess
                }`}
              >
                <ArrowSvg />
              </Box>
            }
          >
            {t(
              haveMoreToShow
                ? 'show-more-text-button'
                : 'show-less-text-button',
            )}
          </TextButton>
        </Box>
      )}
    </Box>
  );
};

export default ClientProjects;
